import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useBattleCardsStore } from 'store/battlecards.store';

import { useMutationDownloadCsvFile } from 'api/mutations/downloadCsvFile.mutation';
import { useMutationExportXerOrMpp } from 'api/mutations/exportXerOrMpp.mutation';
import { useMutationRowsBoardToCsv } from 'api/mutations/extractRowsBoardToCsv.mutation';
import { useMutationRowsBoardToXls } from 'api/mutations/extractRowsBoardToXls.mutation';
import { useQueryCountersData } from 'api/queries/battleCards.query';
import { useQueryListCardsData } from 'api/queries/getListFilteredCards.query';

import { getBooleanFilters, getDateFilters, getFilterData } from './RowsBoard.utils';

interface Props {
    widgetId?: string;
    latestVersionId?: number | undefined;
    localFilters: any;
    table: UseBaseTable['table'];
}

export type UseRowsBoardProvider = ReturnType<typeof useRowsBoardProvider>;

export const useRowsBoardProvider = ({ widgetId, latestVersionId, localFilters, table }: Props) => {
    const { filterData, displayStyle } = useBattleCardsStore();
    const isInPinboard = Boolean(widgetId);

    const source = widgetId ? localFilters : { filterData, displayStyle };

    const pillsData = {
        activityType: getFilterData(source, 'ACTIVITY_TYPE_BOARD'),
        assignee: getFilterData(source, 'ASSIGNEE_CARD_BOARD'),
        Status: getFilterData(source, 'STATUS_CARD_BOARD'),
        criticalPath: getBooleanFilters(source, 'CRITICAL_PATH_USER_CARD_BOARD'),
        delayDriver: getBooleanFilters(source, 'DELAY_DRIVER_CARD_BOARD'),
        delayedPredecessor: getBooleanFilters(source, 'IMPORTANT_PREDECESSOR_OVERDUE'),
        forecastedDurationRisk: getBooleanFilters(source, 'RCF_RISK'),
        constraintDaySet: getBooleanFilters(source, 'CONSTRAINT_DAY_SET'),
        longDuration: getBooleanFilters(source, 'LONG_DURATION'),
        importantLagCard: getBooleanFilters(source, 'IMPORTANT_LAG_CARD_30_BOARD'),
        overdueCardBoard: getBooleanFilters(source, 'OVERDUE_CARD_BOARD'),
        finishProgOverdue: getBooleanFilters(source, 'FINISH_PROG_OVERDUE_CARD_BOARD'),
        startProgOverdue: getBooleanFilters(source, 'START_PROG_OVERDUE_CARD_BOARD'),
        setBack: getBooleanFilters(source, 'SET_BACK'),
        importantStalled: getBooleanFilters(source, 'IMPORTANT_STALLED'),
        anyIndicator: getBooleanFilters(source, 'ANY_INDICATOR'),
        important: getFilterData(source, 'IMPORTANT_CARD_BOARD'),
        urgent: getFilterData(source, 'URGENT_TYPE_CARD_BOARD'),
        tags: getFilterData(source, 'TAG_CARD_BOARD'),
        tagsExclude: getFilterData(source, 'TAG_CARD_BOARD_EXCLUDE'),
        cardSearch: getFilterData(source, 'TEXT_SEARCH_CARD_BOARD'),
        flags: { isAdvancedTextSearch: false },
        finishDate: getDateFilters(source, 'END_DATE_CARD_BOARD', 'endDatesRange'),
        activityCodes: getFilterData(source, 'ACTIVITY_CODES'),
        activityCodesExclude: getFilterData(source, 'ACTIVITY_CODES_EXCLUDE'),
        wbs: getFilterData(source, 'WBS_SEARCH'),
        // Optional
        milestoneFragnet: getFilterData(source, 'FRAGNET_SEARCH'),
        activityForwardFragnet: getFilterData(source, 'FRAGNET_FORWARD_SEARCH'),
        cardType: getFilterData(source, 'TYPE_CARD_BOARD'),
        completionPlanSegment: getFilterData(source, 'FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD'),
        duration: getFilterData(source, 'DURATION_CARD_BOARD'),
        float: getFilterData(source, 'TOTAL_FLOAT_CARD_BOARD'),
        hasAttachments: getBooleanFilters(source, 'DOCLINK_CARD_BOARD'),
        hasComments: getDateFilters(source, 'COMMENT_CARD_BOARD', 'commentRange'),
        commenter: getFilterData(source, 'COMMENTER_CARD_BOARD'),
        mentioned: getFilterData(source, 'MENTIONED_USER_CARD_BOARD'),
        lagLead: getFilterData(source, 'LAG_CARD_BOARD'),
        relatedActivity: getFilterData(source, 'ACTIVITY_NAME_CARD_BOARD'),
        resourceType: getFilterData(source, 'RESOURCE_TYPE_CARD_BOARD'),
        startDate: getDateFilters(source, 'START_DATE_CARD_BOARD', 'startDatesRange'),
        submittedCompletion: getFilterData(source, 'SUBMITTED_COMPLETION_CARD_BOARD'),
        trackedActivity: getBooleanFilters(source, 'TRACKED_CARD_BOARD'),
        updatedBy: getFilterData(source, 'UPDATED_USER_CARD_BOARD'),
        wbsLevel: getFilterData(source, 'WBS_LEVEL_CARD_BOARD'),
        resourceName: source.filterData?.filter.filters.find((item) => item.name === 'RESOURCE_NAME_CARD_BOARD')?.label,
        baselineStartDate: getDateFilters(source, 'BASELINE_START_DATE_CARD_BOARD', 'baselineStartDatesRange'),
        baselineEndDate: getDateFilters(source, 'BASELINE_END_DATE_CARD_BOARD', 'baselineEndDatesRange'),
        cluster: getFilterData(source, 'CLUSTER_CARD_BOARD'),
    };

    const pills = useGetPills({ widgetId, latestVersionId, filterData: source?.filterData, pillsData });

    const getListCardsDataPayload = {
        cardCachedFilter: source?.filterData,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        sortingRequestList: table.sorting,
    };

    return {
        ui: {
            state: {
                filterData: source?.filterData,
                displayStyle: source?.displayStyle,
            },
            pills: pills.newFormatPills,
            isInPinboard,
        },
        api: {
            countersData: useQueryCountersData(source?.filterData),
            listCardsData: {
                instance: useQueryListCardsData(getListCardsDataPayload),
                payload: getListCardsDataPayload,
            },
            downloadCsvFile: {
                instance: useMutationDownloadCsvFile(),
            },
            rowsBoardToCsv: {
                instance: useMutationRowsBoardToCsv(),
            },
            rowsBoardToXls: {
                instance: useMutationRowsBoardToXls(),
            },
            exportXerOrMpp: {
                instance: useMutationExportXerOrMpp(),
            },
        },
    };
};

const useGetPills = ({ widgetId, latestVersionId, filterData, pillsData }) => {
    const backwardCompatibleFilter = filterData.cardCachedFilter
        ? filterData.cardCachedFilter.filter
        : filterData?.filter;
    const { pillsList } = useGlobalFiltersHook({
        widgetId,
        filter: backwardCompatibleFilter,
        latestVersionId,
        source: pillsData,
    });

    return { newFormatPills: pillsList };
};
